import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CONFIG } from '../config'
import { useAuth } from '../contexts/AuthContext'
import { ApiError } from '../types/exception/errorStatus'
import { getErrorMessage } from '../utils/exception/exceptionUtils'
import { useFetch } from './useFetch'

type CountResponse = {
  count: number
}

type CountParams = Record<string, string | number | boolean | undefined>

export const useResourceCount = (
  endpoint: string,
  params: CountParams = {},
  refreshTrigger?: unknown
) => {
  const { authInfo } = useAuth()
  const { fetcher } = useFetch(authInfo?.accessToken, authInfo?.refreshToken)
  const { t } = useTranslation('exceptions')
  const [count, setCount] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [manualRefresh, setManualRefresh] = useState(0)
  const paramsKey = JSON.stringify(params)

  const refetch = useCallback(() => {
    setManualRefresh(current => current + 1)
  }, [])

  useEffect(() => {
    let active = true

    const loadCount = async () => {
      setLoading(true)
      setError(null)

      const query = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) query.set(key, String(value))
      })

      try {
        const response = await fetcher<CountResponse>(
          `${CONFIG.BOUNCE_API}${endpoint}${query.size ? `?${query}` : ''}`
        )

        if (active) setCount(response?.count ?? null)
      } catch (caughtError) {
        if (active) {
          setCount(null)
          setError(
            caughtError instanceof ApiError
              ? getErrorMessage(caughtError.data)
              : t('exceptions:unknown')
          )
        }
      } finally {
        if (active) setLoading(false)
      }
    }

    void loadCount()
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInfo?.accessToken, endpoint, paramsKey, refreshTrigger, manualRefresh, t])

  return { count, loading, error, refetch }
}
